import { Component, Ref, Prop, SimpleEvent } from '@eastsideco/salvo-ts';
import { ThemeComponent } from "@/theme/ThemeComponent";
import { cart } from "@/theme/Cart";
import { CartMain } from "../Cart/CartMain";
import { SideCart } from '../SideCart/SideCart';
import Swiper from 'swiper';

export interface SelectedProducts {
    id: number;
    quantity: number;
}

@Component
export class ShopTheLook extends ThemeComponent {
    // Ref to store buttons and close buttons
    @Ref() shopButton!: HTMLDivElement[];
    @Ref() closeButton!: HTMLDivElement[];
    @Ref submitButton!: HTMLButtonElement[];
    @Ref submitBundle!: HTMLButtonElement[];
    @Ref productId!: number;

    protected loading: boolean = false;
    protected quantityVal: number = 1;
    private cartMain: CartMain = document.body.querySelector('cart-main') as CartMain;
    private sideCart: SideCart | null = document.body.querySelector('side-cart');

    protected itemsPerPage: number = 10;
    protected totalPages: number = 1;
    protected currentPage: number = 1;

    private _onSwiperInit = new SimpleEvent<Swiper>();
    get onSwiperInit() {
        return this._onSwiperInit.asEvent();
    }

    // Map to store Swiper instances per look block
    swipers = new Map<HTMLElement, Swiper>();

    init() {
        this.setupPagination();
        this.openPopup();
        this.closeTrigger();

        for (const button of this.submitButton) {
            this.$listen(button, 'click', async(e) => {
                e.preventDefault();
                this.closePopup();
                const shopLookItem = button.closest('.product-card__footer');
                if (shopLookItem) {
                    const ids = shopLookItem
                        .querySelectorAll('input[type="hidden"]') as
                        NodeListOf<HTMLInputElement>;
                    await this.addToCartBundle(ids);
                }
            });
        }

        // Add event listener for submitBundle button
        for (const button of this.submitBundle) {
            this.$listen(button, 'click', async(e) => {
                e.preventDefault();
                this.closePopup();
                // Find the closest shop-look__product-wrap to the submitBundle button
                const shopLookItem = button.closest('.shop-look__product-wrap');
                if (shopLookItem) {
                    // Select all hidden input fields within the active popup
                    const ids = shopLookItem
                        .querySelectorAll('.shop-look__footer-right input[type="hidden"]') as
                        NodeListOf<HTMLInputElement>;
                    await this.addToCartBundle(ids);
                }
            });
        }
    }

    private openPopup() {
        for (const button of this.shopButton) {
            button.addEventListener('click', () => {
                const shopLookItem = button.closest('.shop-look__item');
                if (shopLookItem) {
                    const popup = shopLookItem.querySelector('.shop-look__popup');
                    if (popup) {
                        popup.classList.toggle('active');
                    }
                }
                this.sliderInitilize();
            });
        }
    }

    private closeTrigger() {
        for (const closeButton of this.closeButton) {
            closeButton.addEventListener('click', () => {
                const popup = closeButton.closest('.shop-look__popup');
                if(popup) {
                    popup.classList.add('closing');
                    popup.addEventListener(
                        'transitionend',
                        () => {
                            popup.classList.remove('closing','active');
                            document.body.classList.remove('popup-active');
                        },
                        { once: true }
                    );
                }
            });
        }
    }

    private closePopup() {
        const activePopup = document.querySelector('.shop-look__popup.active');
        if (activePopup) {
            activePopup.classList.add('closing');
            activePopup.addEventListener(
                'transitionend',
                () => {
                    activePopup.classList.remove('closing', 'active');
                    document.body.classList.remove('popup-active');
                },
                { once: true }
            );
        }
    }

    protected async addToCartBundle(ids: NodeListOf<HTMLInputElement>) {
        try {
            const idsToAdd: { id: number; quantity: number }[] = [];
            const cartCheck: { id: number; inCart: boolean }[] = [];
            for (const [i, id] of ids.entries()) {
                const idUse = parseInt(id.value, 10);
                const itemInCart: boolean = cart.checkCart(idUse);
                const addString = { id: idUse, quantity: 1};
                const checkString = { id: idUse, inCart: itemInCart };
                cartCheck.push(checkString);
                idsToAdd.push(addString);
            }

            await cart.addItems(idsToAdd)
                .then(() => {
                    document.dispatchEvent(new Event('cartChanged'));
                })
                .catch((error: unknown) => {
                    this.$log.error('Cart error', { error });
                });
        } catch (error) {
            this.$log.error('Unexpected cart error', { error });
        }
    }

    private setupPagination() {
        this.totalPages = Math.ceil(document.querySelectorAll('.shop-look__item').length / this.itemsPerPage);
        this.updatePaginationDisplay();

        // Add event listeners for pagination buttons
        document.querySelector('.pagination-prev')?.addEventListener('click', (e) => {
            e.preventDefault();
            this.goToPage(this.currentPage - 1);
        });

        document.querySelector('.pagination-next')?.addEventListener('click', (e) => {
            e.preventDefault();
            this.goToPage(this.currentPage + 1);
        });

        document.querySelector('.pagination-first')?.addEventListener('click', (e) => {
            e.preventDefault();
            this.goToPage(1);
        });

        document.querySelector('.pagination-end')?.addEventListener('click', (e) => {
            e.preventDefault();
            this.goToPage(this.totalPages);
        });
    }

    private goToPage(page: number) {
        if (page < 1 || page > this.totalPages) {return;}
        this.currentPage = page;
        this.updatePaginationDisplay();
        window.scrollTo({ top: 0, behavior: 'smooth' });
    }

    private updatePaginationDisplay() {
        const items = document.querySelectorAll('.shop-look__item') as NodeListOf<HTMLElement>;
        for (const [index, item] of Array.from(items).entries()) {
            const itemPage = Math.floor(index / this.itemsPerPage) + 1;
            item.style.display = itemPage === this.currentPage ? 'block' : 'none';
        }

        // Update pagination info and button states
        const paginationInfo = document.querySelector('.pagination-info');
        if (paginationInfo) {
            paginationInfo.textContent = `${this.currentPage} of ${this.totalPages}`;
        }

        // Toggle button disabled states
        const paginationPrev = document.querySelector('.pagination-prev');
        const paginationNext = document.querySelector('.pagination-next');
        const paginationFirst = document.querySelector('.pagination-first');
        const paginationEnd = document.querySelector('.pagination-end');

        paginationPrev?.classList.toggle('pagination__button--disabled', this.currentPage <= 1);
        paginationNext?.classList.toggle('pagination__button--disabled', this.currentPage >= this.totalPages);
        paginationFirst?.classList.toggle('pagination__button--disabled', this.currentPage <= 1);
        paginationEnd?.classList.toggle('pagination__button--disabled', this.currentPage >= this.totalPages);
    }

    private sliderInitilize() {
        const sliders = document.querySelectorAll<HTMLElement>('.product-slider-js');
        for (const slider of sliders) {
            const parentDiv = slider.closest('.shop-content-js') as HTMLElement;
            if (parentDiv) {
                let swiperInstance = this.swipers.get(slider);

                // Initialize Swiper if not already initialized
                if (!swiperInstance) {
                    swiperInstance = new Swiper(slider, {
                        navigation: {
                            nextEl: parentDiv.querySelector('.swiper-next') as HTMLElement,
                            prevEl: parentDiv.querySelector('.swiper-prev') as HTMLElement,
                        },
                        pagination: true,
                        slidesPerView: 1.15,
                        spaceBetween: 8,
                        speed: 300,
                        breakpoints: {
                            425: {
                                slidesPerView: 1.5,
                                spaceBetween: 12,
                            },
                            768: {
                                slidesPerView: 1,
                                spaceBetween: 64,
                            },
                        },
                    });

                    this.swipers.set(slider, swiperInstance);
                    this._onSwiperInit.dispatch(swiperInstance); // Emit the Swiper instance

                    swiperInstance.on('init', () => {
                        if (swiperInstance) {
                            this.updateNavigationButtons(swiperInstance, parentDiv);
                        }
                    });

                    // Ensure navigation buttons reflect current state
                    this.updateNavigationButtons(swiperInstance, parentDiv);
                }
            }
        }
    }

    updateNavigationButtons(swiperInstance: Swiper, parentDiv: HTMLElement) {
        const prevButton = parentDiv.querySelector('.swiper-prev') as HTMLButtonElement;
        const nextButton = parentDiv.querySelector('.swiper-next') as HTMLButtonElement;

        if (prevButton) {
            if (swiperInstance.isBeginning) {
                prevButton.classList.add('swiper-button-disabled');
                prevButton.disabled = true;
            } else {
                prevButton.classList.remove('swiper-button-disabled');
                prevButton.disabled = false;
            }
        }

        if (nextButton) {
            if (swiperInstance.isEnd) {
                nextButton.classList.add('swiper-button-disabled');
                nextButton.disabled = true;
            } else {
                nextButton.classList.remove('swiper-button-disabled');
                nextButton.disabled = false;
            }
        }
    }

}
